import { useEffect, useRef, useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { setupThreeScene, addBasicLighting } from '@/lib/threeUtils';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { useIsMobile } from '@/hooks/use-mobile';

// Product data
const products = [
  {
    id: 'mai-chat',
    name: 'Mai Chat Agent',
    description: 'Intelligent AI chatbot for customer engagement and support.',
    color: 0x3b82f6, // Blue
    position: new THREE.Vector3(5, 0, 0), // Position for orbit - East
    orbitRadius: 5.0, // Increased for better visibility
    orbitSpeed: 0.2, // Speed of orbit
    link: 'https://maichatagent.com', // External URL for Mai Chat Agent
    logo: '/logo/maichatagentlogo.png', // Using exact format requested
    fallbackGeometry: new THREE.DodecahedronGeometry(1, 2),
    externalLink: true // Flag to indicate external link
  },
  {
    id: 'mai-talk',
    name: 'Mai Talk',
    description: 'AI-powered communication and conversation platform.',
    color: 0x8b5cf6, // Purple
    position: new THREE.Vector3(0, 5, 0), // Position for orbit - North
    orbitRadius: 5.0, // Standardized radius for even distribution
    orbitSpeed: 0.3, // Speed of orbit
    link: 'https://maitalkapp.com', // Link to maitalkapp.com
    logo: '/logo/maitalklogo.png', // Updated to use maitalklogo
    fallbackGeometry: new THREE.OctahedronGeometry(1, 2),
    externalLink: true // Changed to external link
  },
  {
    id: 'mai-tech',
    name: 'Mai Tech Solutions',
    description: 'Custom technology solutions for business growth.',
    color: 0x10b981, // Green
    position: new THREE.Vector3(-5, 0, 0), // Position for orbit - West
    orbitRadius: 5.0, // Standardized radius for even distribution
    orbitSpeed: 0.15, // Speed of orbit
    link: '/case-studies', // Link points to case studies page
    logo: '/logo/maitechsolutions.png', // Mai Tech logo
    fallbackGeometry: new THREE.IcosahedronGeometry(1, 2),
    externalLink: false
  },
  {
    id: 'mai-fit',
    name: 'MaiFit',
    description: 'Fitness community social media app for health enthusiasts.',
    color: 0x22c55e, // Bright Green
    position: new THREE.Vector3(0, -5, 0), // Position for orbit - South
    orbitRadius: 5.0, // Standardized radius for even distribution
    orbitSpeed: 0.25, // Speed of orbit
    link: 'https://maifitapp.com', // Link to maifitapp.com
    logo: '/logo/maifitlogo.png', // MaiFit logo SVG
    fallbackGeometry: new THREE.TetrahedronGeometry(1, 2),
    externalLink: true // External link flag
  }
];

const Hero3D = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const productMeshesRef = useRef<THREE.Mesh[]>([]);
  const frameIdRef = useRef<number | null>(null);
  const composerRef = useRef<EffectComposer | null>(null);
  const orbitTimeRef = useRef<{ time: number }>({ time: 0 });
  const spritesRef = useRef<THREE.Sprite[]>([]);
  
  const [selectedProduct, setSelectedProduct] = useState<typeof products[0] | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const isMobileView = useIsMobile();

  // Initialize the 3D scene
  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    // Set to pure black for better contrast with the logos
    scene.background = new THREE.Color(0x000000);

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      60,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    cameraRef.current = camera;
    
    // Set initial camera position based on device
    const isMobileDevice = window.innerWidth < 768;
    camera.position.z = isMobileDevice ? 15 : 12;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true 
    });
    rendererRef.current = renderer;
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit pixel ratio for better performance
    // Use basic tone mapping to preserve original colors
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.toneMappingExposure = 1.0;
    containerRef.current.appendChild(renderer.domElement);

    // Post-processing setup - no bloom or effects
    const composer = new EffectComposer(renderer);
    composerRef.current = composer;
    
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    // Controls setup
    const controls = new OrbitControls(camera, renderer.domElement);
    controlsRef.current = controls;
    controls.enableDamping = !isMobileDevice; // Disable damping on mobile
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = isMobileDevice ? 0.7 : 0.3; // Faster rotation on mobile
    controls.minDistance = 5;
    controls.maxDistance = 15;
    controls.enablePan = false;
    // Set auto-rotation
    controls.autoRotate = true;
    controls.autoRotateSpeed = isMobileDevice ? 0.3 : 0.2; // Slightly faster on mobile
    
    // Adjust touch controls for better mobile experience
    controls.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_ROTATE
    };

    // Add lighting
    addBasicLighting(scene);
    
    // Add a softer directional light for uniform illumination without affecting colors
    const mainLight = new THREE.DirectionalLight(0xffffff, 0.5);
    mainLight.position.set(5, 5, 7);
    scene.add(mainLight);
    
    // Add ambient light for better visibility of original colors
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    // Create starry background (make it match the tech stack's style)
    createStarryBackground(scene);

    // Create product objects
    createProductObjects(scene, orbitTimeRef);

    // Animate on load
    setTimeout(() => {
      setIsLoaded(true);
    }, 500);

    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current || !renderer || !camera || !composer) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
      composer.setSize(width, height);
      
      // Update camera distance based on screen size
      const isMobileDevice = window.innerWidth < 768;
      if (isMobileDevice) {
        // Move camera further back on mobile to show more of the scene
        camera.position.z = 15;
        
        // Adjust controls for mobile
        if (controlsRef.current) {
          controlsRef.current.enableDamping = false;
          controlsRef.current.rotateSpeed = 0.7;
        }
      } else {
        // Reset to default position on desktop
        camera.position.z = 12;
        
        // Adjust controls for desktop
        if (controlsRef.current) {
          controlsRef.current.enableDamping = true;
          controlsRef.current.rotateSpeed = 0.5;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Animation loop
    const animate = () => {
      frameIdRef.current = requestAnimationFrame(animate);

      // Update controls
      if (controlsRef.current) {
        controlsRef.current.update();
      }

      // Render scene with post-processing
      if (composerRef.current) {
        composerRef.current.render();
      }
    };

    animate();

    // Cleanup
    return () => {
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
        frameIdRef.current = null;
      }

      if (rendererRef.current && containerRef.current) {
        if (containerRef.current.contains(rendererRef.current.domElement)) {
          try {
            containerRef.current.removeChild(rendererRef.current.domElement);
          } catch (e) {
            console.warn('Error removing renderer DOM element:', e);
          }
        }
        
        // Dispose renderer
        rendererRef.current.dispose();
        rendererRef.current = null;
      }

      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      
      // Clean up composer
      composerRef.current = null;
    };
  }, []);

  // Create sky-like starry background with white stars
  const createStarryBackground = (scene: THREE.Scene) => {
    // Create stars - now brighter white, matching tech stack
    const starGeometry = new THREE.BufferGeometry();
    const starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.03, // Match tech stack size
      transparent: true,
      opacity: 0.8, // Match tech stack opacity
    });

    const starVertices = [];
    const starCount = 2000; // Match tech stack count
    const starDistance = 40; // Match tech stack distance

    for (let i = 0; i < starCount; i++) {
      const x = (Math.random() - 0.5) * starDistance;
      const y = (Math.random() - 0.5) * starDistance;
      const z = (Math.random() - 0.5) * starDistance;
      starVertices.push(x, y, z);
    }

    starGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(starVertices, 3)
    );

    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);
  };

  // Create product objects as floating logos orbiting the central logo
  const createProductObjects = (scene: THREE.Scene, orbitRef: React.RefObject<{ time: number }>) => {
    const meshes: THREE.Mesh[] = [];
    const textureLoader = new THREE.TextureLoader();
    const spritesLocalRef: THREE.Sprite[] = [];
    
    // Check if on mobile
    const isMobileDevice = window.innerWidth < 768;
    
    // Sprite scale factors - adjusted for better visibility on mobile
    const centerSpriteScale = isMobileDevice ? 2.2 : 3; // Increased for mobile
    const orbitingSpriteScale = isMobileDevice ? 2.5 : 4.2; // Increased for mobile
    
    // Mobile orbit radius adjustment
    const mobileRadiusMultiplier = 0.7; // Reduce orbit radius on mobile
    
    // Create central Mai Tech logo as the sun
    textureLoader.load(
      '/lovable-uploads/04b305ca-bbd2-4164-ac92-c50eec955c72.png',
      (texture) => {
        // Create a group to hold the logo and text together as one unit
        const centerGroup = new THREE.Group();
        centerGroup.position.set(0, 0, 0);
        scene.add(centerGroup);
        
        // Create sprite material with the logo
        const spriteMaterial = new THREE.SpriteMaterial({ 
          map: texture,
          transparent: true,
          alphaTest: 0.1,
        });
        
        const centerSprite = new THREE.Sprite(spriteMaterial);
        // Adjust size based on device
        centerSprite.scale.set(centerSpriteScale, centerSpriteScale, centerSpriteScale);
        centerSprite.position.set(0, 0, 0);
        
        // Add the logo to the group
        centerGroup.add(centerSprite);
        
        // Very subtle central light that won't affect original colors
        const sunLight = new THREE.PointLight(0xffffff, 0.3, 15); 
        sunLight.position.set(0, 0, 0);
        scene.add(sunLight);
      },
      undefined,
      (error) => {
        console.error('Error loading central logo:', error);
      }
    );
    
    // Create orbiting product logos
    products.forEach((product) => {
      // Create logo as sprite only
      try {
        // Pre-check if the file exists by creating a test image
        const img = new Image();
        img.onload = () => {
          // Logo exists, proceed with loading
          textureLoader.load(
            product.logo,
            (texture) => {
              // Adjust orbit radius for mobile
              const adjustedRadius = isMobileDevice ? 
                product.orbitRadius * mobileRadiusMultiplier : 
                product.orbitRadius;
              
              // Create sprite material with the logo
              const spriteMaterial = new THREE.SpriteMaterial({ 
                map: texture,
                transparent: true,
                alphaTest: 0.1,
                opacity: 1.0, // Full opacity for better visibility
              });
              
              const sprite = new THREE.Sprite(spriteMaterial);
              // Adjust size based on device - increase size for Mai Chat on mobile for easier clicking
              const specificScale = (isMobileDevice && product.id === 'mai-chat') ? 
                orbitingSpriteScale * 1.2 : orbitingSpriteScale;
                
              sprite.scale.set(specificScale, specificScale, specificScale);
              sprite.position.copy(product.position);
              
              // Set specific initial angles to place logos with Mai Talk positioned lower
              let initialAngle = 0;
              if (product.id === 'mai-chat') {
                initialAngle = 0; // right side (0 degrees - East)
              } else if (product.id === 'mai-talk') {
                initialAngle = Math.PI * 0.17; // lower northeast (30 degrees) - well below title
              } else if (product.id === 'mai-tech') {
                initialAngle = Math.PI; // left side (180 degrees - West)
              } else if (product.id === 'mai-fit') {
                initialAngle = Math.PI * 1.5; // bottom (270 degrees - South)
              }
              
              sprite.userData = { 
                isLogo: true,
                productId: product.id,
                orbitRadius: adjustedRadius,
                orbitSpeed: product.orbitSpeed * 0.25, // Slower speed
                initialAngle: initialAngle // Fixed starting position
              };
              
              scene.add(sprite);
              spritesLocalRef.push(sprite);
              
              // Add point light matching product color with very low intensity to just highlight
              const pointLight = new THREE.PointLight(product.color, 0, 0); 
              pointLight.position.copy(product.position);
              scene.add(pointLight);
              
              // Store light for animation
              sprite.userData.light = pointLight;
            },
            undefined,
            (error) => {
              createFallbackSprite(product, scene, spritesLocalRef, isMobileDevice, mobileRadiusMultiplier);
              console.warn(`Error loading logo texture for ${product.id}:`, error);
            }
          );
        };
        img.onerror = () => {
          // Logo doesn't exist, use fallback
          console.warn(`Logo file not found for ${product.id}: ${product.logo}`);
          createFallbackSprite(product, scene, spritesLocalRef, isMobileDevice, mobileRadiusMultiplier);
        };
        img.src = product.logo;
      } catch (error) {
        console.error('Error creating logo:', error);
        createFallbackSprite(product, scene, spritesLocalRef, isMobileDevice, mobileRadiusMultiplier);
      }
    });

    // Store meshes for cleanup
    productMeshesRef.current = meshes;
    
    // Store sprites reference for resize handling
    spritesRef.current = spritesLocalRef;
    
    // Add orbit and rotation animation to sprites
    const animate = () => {
      // Update orbit time
      if (orbitRef.current) {
        orbitRef.current.time += 0.001; // Keep this slow for product orbits
      }
      
      if (spritesRef.current) {
        spritesRef.current.forEach(sprite => {
          if (sprite.userData.orbitRadius) {
            const radius = sprite.userData.orbitRadius;
            const speed = sprite.userData.orbitSpeed;
            const initialAngle = sprite.userData.initialAngle;
            
            // Calculate orbit position with safe access to orbitRef
            const angle = initialAngle + (orbitRef.current?.time || 0) * speed;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            
            // Update sprite position
            sprite.position.set(x, y, 0);
            
            // Update light position if attached
            if (sprite.userData.light) {
              sprite.userData.light.position.set(x, y, 0);
            }
            
            // Make sprites barely rotate - extremely slow rotation
            sprite.rotation.z += 0.0003; // Extremely slow rotation
          }
        });
      }
      
      requestAnimationFrame(animate);
    };
    
    animate();
  };
  
  // Helper function to create fallback sprites
  const createFallbackSprite = (
    product: typeof products[0], 
    scene: THREE.Scene, 
    spritesArray: THREE.Sprite[],
    isMobileDevice: boolean,
    mobileRadiusMultiplier: number
  ) => {
    // Adjust orbit radius for mobile
    const adjustedRadius = isMobileDevice ? 
      product.orbitRadius * mobileRadiusMultiplier : 
      product.orbitRadius;
    
    // Add fallback text label
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = 256;
      canvas.height = 256;
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(product.name, 128, 128);
      
      // Draw a circular border to make it more visible and clickable
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(128, 128, 100, 0, Math.PI * 2);
      ctx.stroke();
      
      const texture = new THREE.CanvasTexture(canvas);
      const spriteMaterial = new THREE.SpriteMaterial({ 
        map: texture,
        transparent: true,
        opacity: 1
      });
      
      const sprite = new THREE.Sprite(spriteMaterial);
      // Adjust size - make it larger for Mai Chat on mobile for easier clicking
      const specificScale = (isMobileDevice && product.id === 'mai-chat') ? 
        3.0 : (isMobileDevice ? 2.5 : 4.2);
        
      sprite.scale.set(specificScale, specificScale, specificScale);
      sprite.position.copy(product.position);
      
      // Set specific initial angles to place logos with Mai Talk positioned lower
      let initialAngle = 0;
      if (product.id === 'mai-chat') {
        initialAngle = 0; // right side (0 degrees - East)
      } else if (product.id === 'mai-talk') {
        initialAngle = Math.PI * 0.17; // lower northeast (30 degrees) - well below title
      } else if (product.id === 'mai-tech') {
        initialAngle = Math.PI; // left side (180 degrees - West)
      } else if (product.id === 'mai-fit') {
        initialAngle = Math.PI * 1.5; // bottom (270 degrees - South)
      }
      
      sprite.userData = { 
        productId: product.id,
        orbitRadius: adjustedRadius,
        orbitSpeed: product.orbitSpeed * 0.25,
        initialAngle: initialAngle
      };
      
      scene.add(sprite);
      spritesArray.push(sprite);
    }
  };
  
  // Handle click on 3D objects - Direct link without modal
  useEffect(() => {
    if (!containerRef.current || !sceneRef.current || !cameraRef.current) return;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Track whether cursor is over a clickable item
    let isOverClickable = false;

    // Debug function to check sprite visibility
    const checkSpritesVisibility = () => {
      if (!spritesRef.current) return;
      
      // Log sprite positions and visibility for debugging
      spritesRef.current.forEach(sprite => {
        if (sprite.userData.productId) {
          console.log(`Sprite ${sprite.userData.productId} position:`, 
            sprite.position, 
            `visible:`, sprite.visible,
            `scale:`, sprite.scale);
        }
      });
    };

    // Set larger touch target size for mobile
    const setLargerTouchTargets = () => {
      if (!spritesRef.current) return;
      
      const isMobileDevice = window.innerWidth < 768;
      if (!isMobileDevice) return;
      
      // Increase collision detection radius for touch input on mobile
      raycaster.params.Sprite = raycaster.params.Sprite || {};
      raycaster.params.Sprite.threshold = 2.0; // Larger threshold for easier touch selection
    };
    
    // Run once on mount
    setLargerTouchTargets();

    // Handle both mouse clicks and touch events
    const handleInteraction = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current || !sceneRef.current || !cameraRef.current) return;

      // Prevent default for all touch events on the container
      if (event.type.startsWith('touch')) {
        event.preventDefault();
      }

      // Get the correct client coordinates based on event type
      let clientX, clientY;
      
      if (event.type === 'touchstart' || event.type === 'touchend') {
        // Use the first touch point
        const touchEvent = event as TouchEvent;
        const touch = touchEvent.changedTouches[0] || touchEvent.touches[0];
        if (!touch) return; // Exit if no touch point found
        
        clientX = touch.clientX;
        clientY = touch.clientY;
      } else {
        clientX = (event as MouseEvent).clientX;
        clientY = (event as MouseEvent).clientY;
      }

      // Calculate mouse position in normalized device coordinates
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

      // Set up raycaster with adjusted parameters for sprite detection
      raycaster.setFromCamera(mouse, cameraRef.current);
      
      // Make sure we cast rays to both scene.children and all nested children
      // This handles the case where sprites are inside groups
      const intersects = raycaster.intersectObjects(sceneRef.current.children, true);

      // Debug: log all intersections to see what's being hit
      const isMobileDevice = window.innerWidth < 768;
      if (isMobileDevice && intersects.length === 0) {
        // If no intersections on mobile, log the mouse position for debugging
        console.log("No intersections found. Mouse position:", mouse, "Num sprites:", spritesRef.current?.length);
        checkSpritesVisibility();
      }

      if (intersects.length > 0) {
        // Log intersections for debugging
        if (isMobileDevice) {
          console.log("Intersections found:", intersects.length);
          intersects.forEach((intersection, i) => {
            console.log(`Intersection ${i}:`, intersection.object.userData);
          });
        }
        
        // First, check for direct product ID in userData
        const directProductHit = intersects.find(obj => 
          obj.object.userData && obj.object.userData.productId
        );
        
        if (directProductHit) {
          console.log("Direct product hit:", directProductHit.object.userData.productId);
          handleProductNavigation(directProductHit.object.userData.productId);
          return;
        }
        
        // If no direct hit, check parent objects
        for (const intersection of intersects) {
          let currentObj: THREE.Object3D | null = intersection.object;
          
          // Look up the parent chain for userData with productId
          while (currentObj) {
            if (currentObj.userData && currentObj.userData.productId) {
              console.log("Parent chain product hit:", currentObj.userData.productId);
              handleProductNavigation(currentObj.userData.productId);
              return;
            }
            currentObj = currentObj.parent;
          }
        }
      }
    };
    
    // Helper function to handle product navigation
    const handleProductNavigation = (productId: string) => {
      const product = products.find(p => p.id === productId);
      if (!product) return;
      
      // Visual feedback on mobile before navigation
      const isMobileDevice = window.innerWidth < 768;
      if (isMobileDevice) {
        // Flash the container briefly to provide visual feedback
        if (containerRef.current) {
          containerRef.current.style.backgroundColor = 'rgba(255,255,255,0.1)';
          setTimeout(() => {
            if (containerRef.current) {
              containerRef.current.style.backgroundColor = '';
            }
            
            // Navigate after visual feedback
            if (product.externalLink) {
              window.open(product.link, '_blank');
            } else {
              window.location.href = product.link;
            }
          }, 100);
          return;
        }
      }
      
      // Immediate navigation on desktop
      if (product.externalLink) {
        window.open(product.link, '_blank');
      } else {
        window.location.href = product.link;
      }
    };

    // Handle mouse move to change cursor
    const handleMouseMove = (event: MouseEvent) => {
      if (!containerRef.current || !sceneRef.current || !cameraRef.current) return;

      // Calculate mouse position in normalized device coordinates
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Set up raycaster
      raycaster.setFromCamera(mouse, cameraRef.current);

      // Find intersections with all objects in the scene
      const intersects = raycaster.intersectObjects(sceneRef.current.children, true);

      // Check if mouse is over a clickable object, including parent checks
      let overClickable = false;
      
      for (const intersection of intersects) {
        let currentObj: THREE.Object3D | null = intersection.object;
        
        // Look up the parent chain for userData with productId
        while (currentObj) {
          if (currentObj.userData && currentObj.userData.productId) {
            overClickable = true;
            break;
          }
          currentObj = currentObj.parent;
        }
        
        if (overClickable) break;
      }

      // Update cursor style
      if (overClickable && !isOverClickable) {
        isOverClickable = true;
        if (containerRef.current) {
          containerRef.current.style.cursor = 'pointer';
        }
      } else if (!overClickable && isOverClickable) {
        isOverClickable = false;
        if (containerRef.current) {
          containerRef.current.style.cursor = 'auto';
        }
      }
    };

    // Add both mouse and touch event listeners
    containerRef.current.addEventListener('click', handleInteraction);
    containerRef.current.addEventListener('touchstart', handleInteraction, { passive: false });
    containerRef.current.addEventListener('touchend', handleInteraction, { passive: false });
    containerRef.current.addEventListener('mousemove', handleMouseMove);
    
    // Add touchmove listener to prevent scrolling while interacting with the 3D scene
    const preventScroll = (e: TouchEvent) => {
      // Only prevent default if the touch is within the container
      const touch = e.touches[0];
      if (!touch || !containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      if (
        touch.clientX >= rect.left && 
        touch.clientX <= rect.right && 
        touch.clientY >= rect.top && 
        touch.clientY <= rect.bottom
      ) {
        e.preventDefault();
      }
    };
    
    containerRef.current.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('click', handleInteraction);
        containerRef.current.removeEventListener('touchstart', handleInteraction);
        containerRef.current.removeEventListener('touchend', handleInteraction);
        containerRef.current.removeEventListener('touchmove', preventScroll);
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  // Handle window resize and device adjustments
  useEffect(() => {
    const handleSpriteResize = () => {
      if (!spritesRef.current || spritesRef.current.length === 0) return;
      
      const isMobileDevice = window.innerWidth < 768;
      const centerSpriteScale = isMobileDevice ? 2.2 : 3; // Increased for mobile
      const orbitingSpriteScale = isMobileDevice ? 2.5 : 4.2;
      
      // Update orbit radii for mobile/desktop
      const mobileRadiusMultiplier = 0.7;
      
      // Adjust sprite sizes based on current screen size
      spritesRef.current.forEach(sprite => {
        // Determine which sprite is the center one (no orbit data means it's the center sprite)
        if (!sprite.userData.orbitRadius) {
          sprite.scale.set(centerSpriteScale, centerSpriteScale, centerSpriteScale);
        } else {
          sprite.scale.set(orbitingSpriteScale, orbitingSpriteScale, orbitingSpriteScale);
          
          // Update orbit radius based on device
          if (sprite.userData.productId) {
            const product = products.find(p => p.id === sprite.userData.productId);
            if (product) {
              sprite.userData.orbitRadius = isMobileDevice ? 
                product.orbitRadius * mobileRadiusMultiplier : 
                product.orbitRadius;
            }
          }
        }
      });
    };
    
    // Run once on mount
    handleSpriteResize();
    
    // Add to existing window resize events
    window.addEventListener('resize', handleSpriteResize);
    window.addEventListener('orientationchange', handleSpriteResize);
    
    return () => {
      window.removeEventListener('resize', handleSpriteResize);
      window.removeEventListener('orientationchange', handleSpriteResize);
    };
  }, [isLoaded]);

  return (
    <div className="relative h-[90vh] md:min-h-screen flex items-center justify-center bg-black pt-16 md:pt-0 w-full">
      {/* 3D Scene Container */}
      <div 
        ref={containerRef} 
        className="absolute inset-0 z-0 touch-auto"
        aria-label="Interactive 3D galaxy with Mai Tech products"
        style={{ cursor: 'auto' }}
      />
      
      {/* "We build the future of tech" heading with animations */}
      <div className="absolute top-20 md:top-24 left-0 right-0 z-10 text-center px-6">
        <h1 
          className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-primary to-purple-500 
                    text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight drop-shadow-[0_2px_5px_rgba(0,0,0,0.5)]
                    leading-tight"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 1.2s ease-out, transform 1.2s ease-out',
            transitionDelay: '0.5s'
          }}
        >
          We build AI solutions and viral APPs
        </h1>
        <div 
          className="h-0.5 bg-gradient-to-r from-blue-400 via-primary to-purple-500 mx-auto mt-2"
          style={{
            width: isLoaded ? '120px' : '0px',
            transition: 'width 1.5s ease-out',
            transitionDelay: '1.2s'
          }}
        ></div>
      </div>
    </div>
  );
};

export default Hero3D; 